import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * Hook para gerenciar o botão voltar nativo do Android
 * Implementa navegação histórica elegante seguindo padrões UX nativos
 */
export function useNativeBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only run on native Android platform
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    // Check if App plugin is available
    if (!App || !App.addListener) {
      console.warn('[useNativeBackButton] Capacitor App plugin not available');
      return;
    }

    // Lista de rotas onde o botão voltar deve minimizar o app em vez de navegar
    const exitRoutes = ['/', '/explorar'];
    
    const handleBackButton = () => {
      const currentPath = location.pathname;
      
      // Se estiver em uma rota de saída, minimize o app
      if (exitRoutes.includes(currentPath)) {
        App.minimizeApp().catch(() => {});
        return;
      }
      
      // Caso contrário, volte na navegação
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        App.minimizeApp().catch(() => {});
      }
    };

    // Registra o listener do botão voltar
    let listener: any;
    try {
      listener = App.addListener('backButton', handleBackButton);
      console.log('[useNativeBackButton] Listener registered');
    } catch (err) {
      console.warn('[useNativeBackButton] Failed to add listener:', err);
    }

    return () => {
      if (listener) {
        listener.then((l: any) => l.remove()).catch(() => {});
      }
    };
  }, [navigate, location.pathname]);
}

/**
 * Componente wrapper que adiciona botão voltar visual para iOS/Web
 * Android já tem o botão nativo do sistema
 */
export function BackButton({ 
  onClick, 
  className = '' 
}: { 
  onClick?: () => void; 
  className?: string;
}) {
  const navigate = useNavigate();
  
  // Detecta se é Android nativo (esconde botão visual)
  const isNativeAndroid = typeof (window as any).Capacitor !== 'undefined' && 
                         (window as any).Capacitor.getPlatform() === 'android';
  
  if (isNativeAndroid) {
    return null; // Android tem botão nativo, não precisa de visual
  }
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`flex items-center gap-1 px-3 py-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm text-gray-700 hover:text-gray-900 transition-all duration-200 active:scale-95 touch-feedback ${className}`}
      aria-label="Voltar"
    >
      <ChevronLeft className="w-5 h-5" />
      <span className="text-sm font-medium">Voltar</span>
    </button>
  );
}
